import type { ProjectAcceptanceResponse } from '@pulse/contracts';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useProjectAcceptance } from '@/domains/projects/api';
import { ProjectTabs } from '@/domains/projects/project-tab';
import { useShell } from '@/domains/shell/api';

/** PRJ-6: the queue shows 25 stories a page. */
export const ACCEPTANCE_PAGE_SIZE = 25;

/** Projects › Acceptance (PRJ-6), built from the prototype's `viewAcceptance()`. An as-of page. */
export function ProjectAcceptancePage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const page = useProjectAcceptance(productSlug);
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow="Project · acceptance"
        title={page.data?.product?.name ?? 'Projects'}
        sub="Stories that are built and waiting for someone to accept them, oldest first."
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      {page.isError ? <span className="sub">There is no project by that name.</span> : null}
      {page.data !== undefined ? <ProjectAcceptanceView data={page.data} productSlug={productSlug} /> : null}
    </>
  );
}

export function ProjectAcceptanceView({ data, productSlug, initialPage = 0 }: { readonly data: ProjectAcceptanceResponse; readonly productSlug: string; readonly initialPage?: number }): React.ReactElement {
  const [pageIndex, setPageIndex] = React.useState(initialPage);
  const pages = Math.max(1, Math.ceil(data.oldest.length / ACCEPTANCE_PAGE_SIZE));
  const first = pageIndex * ACCEPTANCE_PAGE_SIZE;
  const shown = data.oldest.slice(first, first + ACCEPTANCE_PAGE_SIZE);
  const maxBucket = Math.max(1, ...data.buckets.map((bucket) => bucket.count));
  const maxWeek = Math.max(1, ...data.weeks.map((week) => week.intoReview));
  return (
    <>
      <ProjectTabs slug={productSlug} hasBoard hasStories view="acceptance" />
      {data.product === null ? (
        <span className="sub">This project has no story repository, so it has no acceptance queue.</span>
      ) : (
        <>
          <Kpis items={data.tiles.map((tile) => ({ label: tile.label, value: tile.value, sub: tile.sub, warn: tile.warn }))} />
          <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px' }}>
            <section className="card pad stack" style={{ gap: 4 }} aria-labelledby="oq">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: 8 }}>
                <h2 id="oq">Oldest first</h2>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Days since the story moved to in review</span>
              </div>
              {!data.recorded ? <span style={{ color: 'var(--muted)' }}>Not collected yet</span> : null}
              {data.recorded && data.oldest.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing is waiting for acceptance.</span> : null}
              {shown.map((story) => (
                <SourceLink key={story.id} className="prow" href={story.url} style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: '84px minmax(0,1fr) auto' }}>
                  <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent-ink)' }}>
                    {story.id}
                  </span>
                  <span className="stack" style={{ gap: 2 }}>
                    <span>{story.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {story.domain} · {story.assignee}
                    </span>
                  </span>
                  <span className="chip c-warn num">{story.days === null ? 'No dated move' : `${story.days} ${story.days === 1 ? 'day' : 'days'}`}</span>
                </SourceLink>
              ))}
              {data.oldest.length > 0 ? (
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingTop: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Showing {first + 1} to {first + shown.length} of {data.oldest.length}. Accepting a story means checking its acceptance criteria on dev and moving it to accepted.
                  </span>
                  {pages > 1 ? (
                    <div className="row" style={{ gap: 6 }}>
                      <button type="button" className="btn" disabled={pageIndex === 0} onClick={() => setPageIndex(pageIndex - 1)}>
                        Previous
                      </button>
                      <button type="button" className="btn" disabled={pageIndex >= pages - 1} onClick={() => setPageIndex(pageIndex + 1)}>
                        Next
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
            <aside className="stack" style={{ gap: 16 }}>
              <section className="card pad stack" style={{ gap: 10 }}>
                <h2>How long they&apos;ve waited</h2>
                {data.buckets.map((bucket) => (
                  <div key={bucket.label} className="stack" style={{ gap: 6 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span>{bucket.label}</span>
                      <b className="num">{bucket.count}</b>
                    </div>
                    <div className="meter">
                      <i style={{ width: `${((bucket.count / maxBucket) * 100).toFixed(0)}%`, background: `var(${bucket.tone})` }} />
                    </div>
                  </div>
                ))}
              </section>
              <section className="card pad stack" style={{ gap: 10 }}>
                <h2>Into review, per week</h2>
                <div className="mini-bars" role="img" aria-label={`Into review per week: ${data.weeks.map((week) => `${week.label} ${week.intoReview}`).join(', ')}`}>
                  {data.weeks.map((week) => (
                    <div key={week.label} className="stack" style={{ gap: 4, alignItems: 'center', flex: 1 }}>
                      <span className="num" style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {week.intoReview}
                      </span>
                      <i style={{ height: `${((week.intoReview / maxWeek) * 90).toFixed(0)}px` }} />
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{week.label}</span>
                    </div>
                  ))}
                </div>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Accepted per week: {data.weeks.map((week) => week.accepted).join(', ')}.</span>
              </section>
              <section className="card pad stack" style={{ gap: 8 }}>
                <h2>Who owns them</h2>
                {data.owners.map((owner) => (
                  <div key={owner.name} className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{owner.name}</span>
                    <b className="num">{owner.count}</b>
                  </div>
                ))}
              </section>
            </aside>
          </div>
        </>
      )}
    </>
  );
}
